/**
 * CesiumViewer Component
 *
 * Main 3D globe viewer component using Resium (React wrapper for Cesium)
 * Configured for OFFLINE mode without Cesium Ion dependency
 */

import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { Viewer, Camera, ImageryLayer } from 'resium';
import { Viewer as CesiumViewer, OpenStreetMapImageryProvider, UrlTemplateImageryProvider, GridImageryProvider, Color, WebMercatorTilingScheme } from 'cesium';
import { Cartesian3, Math as CesiumMath } from 'cesium';

import { CESIUM_VIEWER_OPTIONS, DEFAULT_CAMERA_POSITION, createTerrainProvider } from '@/lib/cesium-config';
import type { MapViewerProps } from '../types/map.types';
import { cn } from '@/lib/utils';

export interface MapViewerWithMapTypeProps extends MapViewerProps {
  selectedMapType?: string;
  onMapClick?: (position: { lat: number; lng: number; height?: number }) => void;
}

export default function MapViewer({ className, onViewerReady, selectedMapType = '1' }: MapViewerWithMapTypeProps) {
  const viewerRef = useRef<CesiumViewer | null>(null);
  const [currentMapType, setCurrentMapType] = useState(selectedMapType);

  // Create imagery providers for different map types
  const createImageryProvider = useCallback((mapType: string) => {
    switch (mapType) {
      case '1': // OpenStreetMap
       return new OpenStreetMapImageryProvider({
          url: 'https://tile.openstreetmap.org/',
        });
      case '2': // Peta Gelap (CartoDB Dark)
        return new UrlTemplateImageryProvider({
          url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
          subdomains: ['a', 'b', 'c', 'd'],
        });
      case '3': // Peta Satelit (USGS Imagery)
        return new UrlTemplateImageryProvider({
          url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
        });
      case '4': // Peta Terang (USGS Topo)
      default:
        return new UrlTemplateImageryProvider({
          url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
        });
    }
  }, []);

  const createGridProvider = useCallback(
    () =>
      new GridImageryProvider({
        tilingScheme: new WebMercatorTilingScheme(),
        cells: 4,
        color: new Color(0, 0, 0, 0),
        glowColor: new Color(0.2, 0.5, 0.8, 0.1),
        glowWidth: 1,
        backgroundColor: new Color(0, 0, 0, 0),
      }),
    []
  );

  // Create OSM imagery provider for base map
  const osmProvider = useMemo(() => createImageryProvider(currentMapType), [currentMapType, createImageryProvider]);

  // Initial camera position
  const initialPosition = useMemo(
    () =>
      Cartesian3.fromDegrees(
        DEFAULT_CAMERA_POSITION.longitude,
        DEFAULT_CAMERA_POSITION.latitude,
        DEFAULT_CAMERA_POSITION.height
      ),
    []
  );

  // Update map type when selectedMapType prop changes
  useEffect(() => {
    setCurrentMapType(selectedMapType);
  }, [selectedMapType]);

  // Change imagery layer when map type changes
  useEffect(() => {
    if (!viewerRef.current) return;

    const viewer = viewerRef.current;
    const newProvider = createImageryProvider(currentMapType);
    const gridProvider = createGridProvider();

    // Remove all existing imagery layers
    while (viewer.imageryLayers.length > 0) {
      viewer.imageryLayers.remove(viewer.imageryLayers.get(0));
    }

    // Add new imagery layer
    viewer.imageryLayers.addImageryProvider(newProvider);
    // Re-add MGRS grid so overlay tetap ada setelah ganti peta
    viewer.imageryLayers.addImageryProvider(gridProvider);
  }, [currentMapType, createImageryProvider, createGridProvider]);

  // Add MGRS grid to the viewer
  const addMGRSGrid = useCallback((viewer: CesiumViewer) => {
    try {
      const gridProvider = createGridProvider();

      viewer.imageryLayers.addImageryProvider(gridProvider);
      
      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('[CesiumViewer] MGRS grid added successfully');
      }
    } catch (error) {
      console.warn('[CesiumViewer] Failed to add MGRS grid:', error);
    }
  }, []);

  // Handle viewer initialization
  const handleViewerReady = useCallback(
    async (viewer: CesiumViewer) => {
      viewerRef.current = viewer;

      // Configure viewer for better performance
      viewer.scene.globe.enableLighting = false;
      viewer.scene.globe.depthTestAgainstTerrain = false;

      // Load terrain provider asynchronously
      try {
        const terrainProvider = await createTerrainProvider();
        viewer.terrainProvider = terrainProvider;
      } catch (error) {
        console.warn('[CesiumViewer] Failed to load terrain provider:', error);
      }

      // Add MGRS grid overlay
      addMGRSGrid(viewer);

      // Set initial camera position
      viewer.camera.setView({
        destination: Cartesian3.fromDegrees(
          DEFAULT_CAMERA_POSITION.longitude,
          DEFAULT_CAMERA_POSITION.latitude,
          DEFAULT_CAMERA_POSITION.height
        ),
        orientation: {
          heading: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.heading),
          pitch: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.pitch),
          roll: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.roll),
        },
      });

      // Configure home button to use our default position
      if (viewer.homeButton) {
        viewer.homeButton.viewModel.command.beforeExecute.addEventListener((e) => {
          e.cancel = true;
          viewer.camera.flyTo({
            destination: Cartesian3.fromDegrees(
              DEFAULT_CAMERA_POSITION.longitude,
              DEFAULT_CAMERA_POSITION.latitude,
              DEFAULT_CAMERA_POSITION.height
            ),
            orientation: {
              heading: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.heading),
              pitch: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.pitch),
              roll: CesiumMath.toRadians(DEFAULT_CAMERA_POSITION.roll),
            },
            duration: 2.0,
          });
        });
      }

      // Call parent callback if provided
      if (onViewerReady) {
        onViewerReady(viewer);
      }

      // Development logging
      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('[CesiumViewer] Viewer initialized successfully (OFFLINE mode)');
      }
    },
    [onViewerReady, addMGRSGrid]
  );

  return (
    <div className={cn('relative w-full h-full', className)}>
      <Viewer
        full
        ref={(ref) => {
          if (ref?.cesiumElement) {
            handleViewerReady(ref.cesiumElement);
          }
        }}
        {...CESIUM_VIEWER_OPTIONS}
      >
        <Camera position={initialPosition} />
        {/* OpenStreetMap Base Layer */}
        <ImageryLayer imageryProvider={osmProvider} />
      </Viewer>
    </div>
  );
}
